import { generateId } from "@primaria/plugins-core";
import { IActivityHistoryGroup, IActivityHistoryItem, IActivityHistorySubGroup } from "../../domain/model";
import { areSameDiagnostics } from "../../domain/validation/diagnostics/are-same-diagnostics";
import { hasValidDiagnostics } from "../../domain/validation/diagnostics/has-valid-diagnostics";

// Cache para evitar recalcular fechas
const dateStringCache = new Map<string, string>();
const dateObjectCache = new Map<string, Date>();

const getDateString = (dateStr: string): string => {
  if (!dateStringCache.has(dateStr)) {
    dateStringCache.set(dateStr, new Date(dateStr).toDateString());
  }
  return dateStringCache.get(dateStr)!;
};

const getDateObject = (dateStr: string): Date => {
  if (!dateObjectCache.has(dateStr)) {
    dateObjectCache.set(dateStr, new Date(dateStr));
  }
  return dateObjectCache.get(dateStr)!;
};

// Crear una clave base sin servicio (para comparaciones cuando el servicio no está informado)
const createVisitKeyWithoutService = (item: IActivityHistoryItem): string => {
  const professionalKey = `${item.professional?.id || 'null'}-${item.professional?.role?.id || 'null'}-${item.professional?.speciality?.id || 'null'}`;
  const locationKey = `${item.ep?.id || 'null'}-${item.up?.id || 'null'}-${item.center?.id || 'null'}`;
  const dayKey = getDateString(item.date);

  return `${professionalKey}|${locationKey}|${dayKey}`;
};

// Verificar si el servicio está informado (null, undefined o string vacío = no informado)
const hasService = (item: IActivityHistoryItem): boolean => {
  return item.service?.id != null && item.service.id !== "";
};

// Comparar si dos items pueden agruparse juntos
// Si alguno de los items no tiene servicio informado, se ignora el servicio en la comparación
const canGroupTogether = (item1: IActivityHistoryItem, item2: IActivityHistoryItem): boolean => {
  const baseKey1 = createVisitKeyWithoutService(item1);
  const baseKey2 = createVisitKeyWithoutService(item2);

  // Si las claves base no coinciden, no se pueden agrupar
  if (baseKey1 !== baseKey2) return false;

  // Si alguno no tiene servicio informado, se pueden agrupar (ya que la base coincide)
  if (!hasService(item1) || !hasService(item2)) return true;

  // Si ambos tienen servicio, deben coincidir
  return item1.service?.id === item2.service?.id;
};

const withinEightHours = (date1: string, date2: string): boolean => {
  const time1 = getDateObject(date1).getTime();
  const time2 = getDateObject(date2).getTime();
  return Math.abs(time1 - time2) <= 8 * 60 * 60 * 1000; // 8 hours in milliseconds
};

function groupByValidDiagnostics(groups: IActivityHistoryGroup[]) {
  groups.forEach((group) => {
    const subGroups: IActivityHistorySubGroup[] = [];
    const remainingItems: IActivityHistoryItem[] = [];

    group.items.forEach((item) => {
      const diagnostics = item.diagnostics;
      const allDiagnosticsValid = diagnostics?.length > 0 && hasValidDiagnostics(diagnostics);

      if (allDiagnosticsValid) {
        let addedToSubGroup = false;

        // Optimización: usar for...of con break para salir temprano
        for (const subGroup of subGroups) {
          const firstSubGroupItem = subGroup.items[0];

          if (areSameDiagnostics(firstSubGroupItem.diagnostics, diagnostics)) {
            subGroup.items.push(item);
            addedToSubGroup = true;
            break;
          }
        }

        if (!addedToSubGroup) {
          subGroups.push({
            idSubGroup: generateId(),
            items: [item],
          });
        }
      } else {
        remainingItems.push(item);
      }
    });

    group.items = remainingItems;
    group.subGroups = subGroups;
  });
}

const groupActivityHistoryItems = (items: IActivityHistoryItem[]): IActivityHistoryGroup[] => {
  if (!items?.length) return [];

  // Clear caches
  dateStringCache.clear();
  dateObjectCache.clear();

  // Sort all items by date (most recent first)
  const sortedItems = [...items].sort((a, b) =>
    getDateObject(b.date).getTime() - getDateObject(a.date).getTime()
  );

  // Use a Map to track groups by base visit key (without service) for O(1) lookup
  const groupsByBaseKey = new Map<string, IActivityHistoryGroup[]>();
  const allGroups: IActivityHistoryGroup[] = [];

  for (const item of sortedItems) {
    const itemBaseKey = createVisitKeyWithoutService(item);
    const itemTime = getDateObject(item.date).getTime();
    let addedToExistingGroup = false;

    // Get all groups for this base key (O(1) lookup)
    const candidateGroups = groupsByBaseKey.get(itemBaseKey) || [];

    // Try to add to an existing group with the same base key
    for (const group of candidateGroups) {
      const firstItem = group.items[0];
      const lastItem = group.items[group.items.length - 1];

      if (!firstItem || !lastItem) continue;

      // Check if items can be grouped together (handles service logic)
      if (!canGroupTogether(firstItem, item)) continue;

      // Verificar si está dentro del rango de 8 horas
      if (
        withinEightHours(firstItem.date, item.date) &&
        withinEightHours(lastItem.date, item.date)
      ) {
        // Check for temporal conflicts with other groups
        const groupStartTime = getDateObject(lastItem.date).getTime();
        const groupEndTime = getDateObject(firstItem.date).getTime();
        const newGroupStartTime = Math.min(groupStartTime, itemTime);
        const newGroupEndTime = Math.max(groupEndTime, itemTime);

        let hasTemporalConflict = false;

        // Only check groups with different base keys (different professionals/locations)
        for (const [otherKey, otherGroups] of groupsByBaseKey) {
          if (otherKey === itemBaseKey) continue;

          for (const otherGroup of otherGroups) {
            for (const otherItem of otherGroup.items) {
              const otherTime = getDateObject(otherItem.date).getTime();
              if (otherTime > newGroupStartTime && otherTime < newGroupEndTime) {
                hasTemporalConflict = true;
                break;
              }
            }
            if (hasTemporalConflict) break;
          }
          if (hasTemporalConflict) break;
        }

        if (!hasTemporalConflict) {
          group.items.push(item);
          // Items are already sorted in sortedItems, no need to re-sort
          addedToExistingGroup = true;
          break;
        }
      }
    }

    // Create new group if not added to existing one
    if (!addedToExistingGroup) {
      const newGroup: IActivityHistoryGroup = {
        idGroup: Math.random().toString(36).substring(2, 11),
        items: [item],
        subGroups: [],
      };

      allGroups.push(newGroup);

      // Add to the map using base key (without service)
      const keyGroups = groupsByBaseKey.get(itemBaseKey);
      if (keyGroups) {
        keyGroups.push(newGroup);
      } else {
        groupsByBaseKey.set(itemBaseKey, [newGroup]);
      }
    }
  }

  // Sort groups by the most recent item date in each group
  allGroups.sort((a, b) => {
    const aFirstItem = a.items[0];
    const bFirstItem = b.items[0];
    if (!aFirstItem || !bFirstItem) return 0;
    return getDateObject(bFirstItem.date).getTime() - getDateObject(aFirstItem.date).getTime();
  });

  // Create subgroups by matching diagnostics
  groupByValidDiagnostics(allGroups);

  return allGroups;
};

export { groupActivityHistoryItems };
